import { useMemo } from 'react';

import { narrowGrantedCapabilities, type StudioCapabilityName } from '../../../engine/scripting/capabilityNames';
import { useSharedModeDocument } from '../panel-system/sharedModeDocument';
import { GAMESETUP_DOC_FALLBACK, type GameSetupDoc } from '../../services/gameSetup';

/**
 * The project's own declared capability grant, read directly from the
 * shared Game Setup document - not `scriptConsoleStore.allowedCapabilities`,
 * which is scoped to a live Preview session and is absent whenever the Code
 * workspace is open (Code and Preview are mutually exclusive routes), so
 * reading that here made every diagnostic see "not configured".
 *
 * Memoised on the document field's identity: `narrowGrantedCapabilities`
 * returns a new Set every call, and the editor panel re-renders on every
 * keystroke - an unmemoised value would defeat HollowScriptFileEditor's
 * diagnostics debounce entirely, since its marker effect depends on this by
 * reference. Also reports whether the real document has loaded yet, so a
 * caller can hold off anything grant-sensitive until it has.
 */
export function useProjectScriptCapabilities(): { capabilities: ReadonlySet<StudioCapabilityName> | undefined; loaded: boolean } {
  const { data, loading } = useSharedModeDocument<GameSetupDoc>('gamesetup', GAMESETUP_DOC_FALLBACK);
  const capabilities = useMemo(() => narrowGrantedCapabilities(data.scriptCapabilities), [data.scriptCapabilities]);
  return { capabilities, loaded: !loading };
}
